/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useGlobalSearch, useProcess } from "@/global-data";
import { useDeletePopup } from "@/global-data/use-delete-popup";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { useImportExport } from "@/hooks/use-import-export";
import { useToast } from "@/hooks/use-toast";
import { getUserInfo } from "@/lib/token";
import { wait } from "@/lib/wait";
import { apiServices } from "@/services/api.services";
import {
  companies,
  erpSku,
  retailers,
  synonym,
  workflow,
} from "@/services/global.services";
import { useEffect, useMemo, useState } from "react";

export const useMutations = () => {
  const { toast } = useToast();

  const API_SERVICE = retailers;
  const { company_id, user_id } = useMemo(() => {
    const tempUserData = getUserInfo();
    return {
      company_id: tempUserData?.company_id,
      user_id: tempUserData?.user_id || "",
    };
  }, []);
  const { setProcess } = useProcess();
  const [skeltonLoading, setSkeltonLoading] = useState(true);
  const [listData, setListData] = useState([]);
  const [sncErpSku, setSncErpSku] = useState([]);
  const [companyData] = useState([]);
  const [filter, setFilter] = useState({});
  const [defaultWorkflow, setDefaultWorkflow] = useState(undefined);
  const [formPopupOpen, setFormPopupOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(undefined);
  const [activeRecordData, setActiveRecordData] = useState<any>(undefined);
  const [tableColumn, setTableColumn] = useState([
    "retailer_name",
    "billing_address",
    "matching_keys",
    "shipping_address",
    "uen",
  ]);

  const [domainList, setDomainList] = useState([]);
  const [attributesList, setAttributesList] = useState<any[]>([]);

  const { setSearchProgress, search } = useGlobalSearch();
  const [searchDebounced] = useDebouncedValue(search, 200);

  const [sortBy, setSortBy] = useState("cts");
  const [orderBy, setOrderBy] = useState(false);

  const { onDelete, status, identity, clearDelete } = useDeletePopup();

  const [paginationData, setPaginationData] = useState({
    page: 1,
    perPage: 10,
    totalItem: 1,
  });

  const [errorMessage, setErrorMessage] = useState<any>({
    title: "",
    message: "",
    open: false,
  });

  const closeErrorMessage = () => {
    setErrorMessage({
      title: "Error",
      message: "",
      open: false,
    });
  };

  //On Load Component
  useEffect(() => {
    (async () => {
      await loadingList({ searchText: "" });
      await loadingDomain();
      await loadingSettings();
      await wait(1000);
      setSkeltonLoading(false);
      setProcess(false);
    })();
    return () => {
      closeErrorMessage();
    };
  }, []);

  const loadingSettings = async () => {
    const payload = {
      data: {
        filter_by: {
          company_id: [company_id],
          search_text: "",
          exact_match: false,
        },
      },
      company_id: company_id,
      user_id: user_id,
    };
    const { data, error, message } = await apiServices<any>(
      companies.get,
      "post",
      payload,
    );

    if (data) {
      const tempData: any = data.result.metadata[companies.key][0];
      if (tempData?.settings?.default_workflow) {
        await loadingWorkflow(tempData?.settings?.default_workflow);
      }
    }
    if (error) {
      toast({
        title: "Error",
        variant: "destructive",
        description: error.status?.message || message,
      });
    }
    return { data, error };
  };
  const loadingWorkflow = async (currentId: string) => {
    const payload = {
      data: {
        workflow_id: currentId,
      },
      company_id: company_id,
      user_id: user_id,
    };
    const { data, error, message } = await apiServices<any>(
      workflow.get,
      "post",
      payload,
    );

    if (data) {
      const tempData: any = data.result.metadata[workflow.key][0];
      console.log({ workflowData: tempData });
      if (tempData?.synonym_mapping && tempData?.synonym_mapping !== "") {
        setDefaultWorkflow(tempData?.synonym_mapping);
      }
    }
    if (error) {
      toast({
        title: "Error",
        variant: "destructive",
        description: error.status?.message || message,
      });
    }
    return { data, error };
  };

  //handle delete
  useEffect(() => {
    if (status === "cancel") {
      setDeleteId(undefined);
      clearDelete();
    }
    if (status === "delete" && identity === "user") {
      (async () => {
        setProcess(true);
        const deleteResponse = await deleteRecord();
        if (deleteResponse.data) {
          await loadingList({});
          toast({
            title: "Success",
            variant: "success",
            description:
              deleteResponse?.data.result.metadata.message ??
              deleteResponse?.data?.status?.msg ??
              deleteResponse?.data?.status?.message,
          });
        }
        clearDelete();
        setDeleteId(undefined);
        setProcess(false);
      })();
    }
  }, [status]);

  const deleteRecord = async () => {
    const payload = {
      data: {
        retailer_id: deleteId,
        company_id: company_id,
      },
      company_id: company_id,
    };
    const { data, error, message } = await apiServices<any>(
      API_SERVICE.delete,
      "post",
      payload,
    );

    if (error) {
      toast({
        title: "Error",
        variant: "destructive",
        description: error.status?.message || message,
      });
    }
    return { data, error };
  };

  const loadingDomain = async () => {
    const payload = {
      data: {
        filter_by: {
          search_text: "",
          exact_match: false,
        },
      },
      company_id: company_id,
      user_id: user_id,
    };
    const { data, error, message } = await apiServices<any>(
      synonym.get,
      "post",
      payload,
    );

    if (data) {
      const localList: any = [];
      const listData = data.result.metadata[synonym.key];
      for (const item of listData) {
        localList.push({ label: item.domain_name, value: item.domain_name });
      }
      setDomainList(localList);
      // const tempList: any[] = extractAttributeNames(listData);
      // console.log({ tempList });
      // setAttributesList(tempList);
      setAttributesList(listData);
    }
    if (error) {
      toast({
        title: "Error",
        variant: "destructive",
        description: error.status?.message || message,
      });
    }
    return { data, error };
  };

  //Handle Search
  useEffect(() => {
    if (skeltonLoading === false) {
      (async () => {
        setSearchProgress(true);
        setPaginationData((prev) => ({ ...prev, page: 1 }));
        await loadingList({ page: 1 });
        setSearchProgress(false);
      })();
    }
  }, [searchDebounced]);

  const onFormOpenChange = (prop: boolean) => {
    setFormPopupOpen(prop);
  };

  const onPageChange = async (prop: number) => {
    setPaginationData((prev) => ({ ...prev, page: prop }));
    setProcess(true);
    await loadingList({ page: prop });
    setProcess(false);
  };
  const onPerPageChange = async (prop: number) => {
    setPaginationData((prev) => ({ ...prev, perPage: prop, page: 1 }));
    setProcess(true);
    await loadingList({ perPage: prop, page: 1 });
    setProcess(false);
  };

  const permission: any = [];
  const onAction = async ({ action, data }: any) => {
    if (action === "edit") {
      setActiveRecordData(data);
      onFormOpenChange(true);
    }
    if (action === "delete") {
      setActiveRecordData(data);
      setDeleteId(data.retailer_id);
      onDelete({
        description:
          "This action cannot be undone. This will permanently delete the record and remove its data from our system.",
        id: data.user_id,
        identity: "user",
        popupStatus: true,
        title: "Are you sure you want to delete this retailer?",
      });
      await loadingList({});
    }
  };

  useEffect(() => {
    if (formPopupOpen === false) {
      setActiveRecordData(undefined);
    }
  }, [formPopupOpen]);

  const sortingHandler = async (prop: string) => {
    const tempSortBy = prop;
    const tempOrderBy = sortBy == prop ? !orderBy : true;
    console.log({ tempSortBy, tempOrderBy });
    setProcess(true);
    setSortBy(tempSortBy);
    setOrderBy(tempOrderBy);
    await loadingList({
      sortByProp: tempSortBy,
      orderByProp: tempOrderBy,
    });
    setProcess(false);
  };

  const loadSncErpSku = async () => {
    const payload = {
      data: {
        key: " ",
      },
      company_id: company_id,
      user_id: user_id,
    };
    const { data, error, message } = await apiServices<any>(
      erpSku.get,
      "post",
      payload,
    );

    setProcess(true);

    if (data) {
      setSncErpSku(data.result.metadata[erpSku.key]);
    }
    if (error) {
      toast({
        title: "Error",
        variant: "destructive",
        description: error.status?.message || message,
      });
    }
    setProcess(false);
    return { data, error };
  };

  const loadingList = async ({
    perPage,
    page,
    filterReset,
    makeFilter,
    searchText,
    orderByProp,
    sortByProp,
  }: {
    perPage?: number;
    page?: number;
    filterReset?: boolean;
    makeFilter?: boolean;
    searchText?: string;
    sortByProp?: string;
    orderByProp?: boolean;
  }) => {
    let apiFilter = {};

    if (!filterReset) {
      apiFilter = filter;
    }
    let page_no = page || paginationData.page;

    if (makeFilter) {
      page_no = 1;
    }

    const tempSortBy = sortByProp ?? sortBy;
    const tempOrderBy = orderByProp ?? orderBy;

    const payload = {
      data: {
        filter_by: {
          ...apiFilter,
          search_text: searchText ?? searchDebounced,
          exact_match: false,
          pagination: {
            page_no: page_no,
            no_of_recs: perPage || paginationData.perPage,
            sort_by: tempSortBy,
            order_by: tempOrderBy,
          },
        },
      },
      company_id: company_id,
      user_id: user_id,
    };
    const { data, error, message } = await apiServices<any>(
      API_SERVICE.get,
      "post",
      payload,
    );

    if (data) {
      setListData(data.result.metadata[API_SERVICE.key]);
      setPaginationData((prev) => ({
        ...prev,
        totalItem: data?.result?.metadata?.total_records || 1,
      }));
    }
    if (error) {
      toast({
        title: "Error",
        variant: "destructive",
        description: error.status?.message || message,
      });
    }
    return { data, error };
  };

  const { exportDocument, importDocument } = useImportExport({
    API_SERVICE,
    loadingList,
  });
  const onFormSubmit = async (prop: any): Promise<any> => {
    let formData = {};
    // const method = activeRecordData ? "patch" : "post";
    // const url = activeRecordData ? API_SERVICE.update : API_SERVICE.post;
    if (activeRecordData) {
      formData = {
        retailer_id: activeRecordData?.retailer_id,
        retailer_name: prop.retailer_name,
        matching_keys: prop.matching_keys,
        // date_format: prop.date_format,
        synonym_mapping: prop.synonym_mapping,
        map_using_filename: prop.map_using_filename,
        auto_generate_reference_no: prop.auto_generate_reference_no,
        settings: prop.settings,
        retailer_code: prop.retailer_code,
        divide_doc_to_chunks: prop.divide_doc_to_chunks,
        child_doc_synonym_mapping:
          prop.divide_doc_to_chunks || prop.synonym_mapping !== ""
            ? prop.child_doc_synonym_mapping
            : "",
      };
    } else {
      formData = {
        is_new: true,
        retailer_name: prop.retailer_name,
        matching_keys: prop.matching_keys,
        // date_format: prop.date_format,
        synonym_mapping: prop.synonym_mapping,
        map_using_filename: prop.map_using_filename,
        auto_generate_reference_no: prop.auto_generate_reference_no,
        settings: prop.settings,
        retailer_code: prop.retailer_code,
        divide_doc_to_chunks: prop.divide_doc_to_chunks,
        child_doc_synonym_mapping:
          prop.divide_doc_to_chunks || prop.synonym_mapping !== ""
            ? prop.child_doc_synonym_mapping
            : "",
      };
    }

    const payload = {
      data: formData,
      company_id: company_id,
      user_id: user_id,
    };

    const { data, error, message } = await apiServices<any>(
      API_SERVICE.post,
      "post",
      payload,
    );

    if (data) {
      const response = await loadingList({});
      if (response.data) {
        console.log(
          data.result.metadata.message ??
            data?.status?.msg ??
            data?.status?.message,
        );
        toast({
          title: "Success",
          variant: "success",
          description:
            data.result.metadata.message ??
            data?.status?.msg ??
            data?.status?.message,
        });
      }
    }

    if (error) {
      setProcess(false);
    }

    if (error) {
      console.log({
        data,
        error,
        message,
        mainError: error.status?.message,
      });

      // toast({
      //   title: "Error",
      //   variant: "destructive",
      //   description: error.status?.message || message,
      // });
      setErrorMessage({
        title: "Error",
        message: error.status?.message || message,
        open: true,
      });
    }
    return { data, error };
  };
  const onMapUsingFilenameUpdate = async (prop: any): Promise<any> => {
    setProcess(true);

    const formData = {
      retailer_id: prop?.retailer_id,
      retailer_name: prop.retailer_name,
      matching_keys: prop.matching_keys,
      // date_format: prop.date_format,
      synonym_mapping: prop.synonym_mapping,
      map_using_filename: prop.map_using_filename,
      auto_generate_reference_no: prop.auto_generate_reference_no,
      settings: prop.settings,
    };

    const payload = {
      data: formData,
      company_id: company_id,
      user_id: user_id,
    };

    const { data, error, message } = await apiServices<any>(
      API_SERVICE.post,
      "post",
      payload,
    );

    if (data) {
      const response = await loadingList({});
      if (response.data) {
        toast({
          title: "Success",
          variant: "success",
          description: data.status.msg,
        });
      }
    }

    if (error) {
      setProcess(false);
    }

    if (error) {
      toast({
        title: "Error",
        variant: "destructive",
        description: error.status?.message || message,
      });
    }
    setProcess(false);

    return { data, error };
  };

  const filterRequest = async () => {
    setProcess(true);
    await loadingList({ makeFilter: true });
    setProcess(false);
  };
  const clearFilter = async () => {
    setFilter({});
    setProcess(true);
    await loadingList({ filterReset: true });
    setProcess(false);
  };

  return {
    skeltonLoading,
    listData,
    onPageChange,
    onPerPageChange,
    paginationData,
    permission,
    onAction,
    activeRecordData,
    onFormSubmit,
    onFormOpenChange,
    formPopupOpen,
    tableColumn,
    setTableColumn,
    companyData,
    filter,
    setFilter,
    filterRequest,
    clearFilter,
    domainList,
    onMapUsingFilenameUpdate,
    errorMessage,
    closeErrorMessage,
    attributesList,
    defaultWorkflow,
    exportDocument,
    importDocument,
    sortingHandler,
    sortBy,
    orderBy,
    loadSncErpSku,
    sncErpSku,
  };
};
